import { useNavigate } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";

import {
  Container,
  BackButton,
  Title,
  Section,
  SectionTitle,
  CorePrinciplesGrid,
  PrincipleText,
  StepTitle,
  StepContent,
  ImagePlaceholder,
  Column,
  PrincipleTextLight,
  Video,
  Videos,
} from "./TrainTutorialPage.styles";

import Train01 from "../../assets/images/train1.png";
// import Train02 from "../../assets/images/train2.png";
import Train03 from "../../assets/images/train3.png";
import Train04 from "../../assets/images/train4.png";
import Video01 from "../../assets/videos/zoomout.mp4";
import Video02 from "../../assets/videos/hardcut.mp4";
import Video03 from "../../assets/videos/1.mp4";
import Video04 from "../../assets/videos/2.mp4";
import Video05 from "../../assets/videos/3.mp4";
import Video06 from "../../assets/videos/4.mp4";
import Video07 from "../../assets/videos/5.mp4";
import Video08 from "../../assets/videos/6.mp4";
import Video09 from "../../assets/videos/7.mp4";
import Video10 from "../../assets/videos/8.mp4";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/user/slice";

const TrainTutorialPage = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const handleBack = () => {
    navigate(-1);

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  };

  const corePrinciples = [
    {
      number: 1,
      text: "Train the motion, not the ID. This template is for image2video: the person comes from the first frame image; training only teaches how it moves.",
    },
    {
      number: 2,
      text: "The generated video is always 5s, so your action must complete within five seconds.",
    },
    {
      number: 3,
      text: "The model learns mostly from the training videos, and the prompt is there to briefly describe the action. The videos shape the result, but the prompt still needs to clearly say what is happening.",
    },
    {
      number: 4,
      text: "Consistency first. Quality > quantity. Use the same action and rhythm across clips. It's better to have 5 clean clips than 20 noisy, mismatched ones.",
    },
    {
      number: 5,
      text: 'Improve generalization with diverse starting states (e.g., front/back starts, single/dual subjects, "no X → has X" transitions), while keeping the core action identical.',
    },
    {
      number: 6,
      text: "Attributes such as bust shape/size, color tones, hairy vs. non-hairy, facial expression, etc. are determined by the data distribution in your training videos.",
    },
  ];

  return (
    <MainContainer>
      <BackButton onClick={handleBack}>
        <ChevronSvg />
        Back to Train
      </BackButton>
      <Container>
        <Title>Train Tutorial</Title>
        <Section>
          <SectionTitle>Update</SectionTitle>
          <CorePrinciplesGrid>
            <PrincipleText>
              • For templates that progress from close-up face shots to complex
              scenes and actions, the training data should follow the same
              structure — starting with face images and then expanding outward.
              The transition from face-focused shots to broader scenes can be
              implemented in two ways: either a smooth zoom-out transition or a
              hard cut between shots.
              <br />
              <br />
              Transitions within a single training video should always feature
              the same individual, while different training videos should use
              different individuals to maintain strong facial consistency in the
              template. The larger the face appears in the frame — both before
              and after the transition — the better the facial consistency will
              be preserved.
            </PrincipleText>
          </CorePrinciplesGrid>
          <Videos>
            <Video src={Video01} autoPlay={false} loop controls muted />
            <Video src={Video02} autoPlay={false} loop controls muted />
          </Videos>
        </Section>
        <Section>
          <SectionTitle>Core Principles</SectionTitle>
          <CorePrinciplesGrid>
            {corePrinciples.map((principle, i) => (
              <PrincipleText key={i}>• {principle.text}</PrincipleText>
            ))}
          </CorePrinciplesGrid>
        </Section>

        <Section>
          <StepTitle>1. Train Model</StepTitle>
          <StepContent>
            <ImagePlaceholder src={Train01} alt="Train tutorial image 01" />

            <Column>
              <SectionTitle>What to fill in</SectionTitle>

              <Column $small>
                <PrincipleText>• Task Name</PrincipleText>
                <PrincipleText>Internal identifier.</PrincipleText>
              </Column>

              <Column $small>
                <PrincipleText>• Prompt</PrincipleText>
                <PrincipleText>
                  Write a clear action description that exactly matches the
                  videos, for example:
                </PrincipleText>
                <PrincipleText>
                  The woman turns around and walks away.
                </PrincipleText>
              </Column>

              <Column $small>
                <PrincipleText>• Training Videos</PrincipleText>
                <PrincipleText>
                  Every clip must show the same action (exactly matching the
                  prompt). Recommended constraints: 10~20 clips, 3~5 s each,
                  resolution ≥ 360p, ≤ 20 MB per clip.
                </PrincipleText>
              </Column>

              <Column $small>
                <PrincipleText>• Testing Images (6~12)</PrincipleText>
                <PrincipleText>
                  Pick 6~12 different image from the library to verify that the
                  learned motion transfers to new IDs.
                </PrincipleText>
              </Column>
            </Column>
            <Videos>
              <Video src={Video03} autoPlay={false} loop controls muted />
              <Video src={Video04} autoPlay={false} loop controls muted />
              <Video src={Video05} autoPlay={false} loop controls muted />
              <Video src={Video06} autoPlay={false} loop controls muted />
            </Videos>
            <Videos>
              <Video src={Video07} autoPlay={false} loop controls muted />
              <Video src={Video08} autoPlay={false} loop controls muted />
              <Video src={Video09} autoPlay={false} loop controls muted />
              <Video src={Video10} autoPlay={false} loop controls muted />
            </Videos>
            {/* <ImagePlaceholder
              style={{ marginTop: 16 }}
              src={Train02}
              alt="Train tutorial image 02"
            /> */}
          </StepContent>
        </Section>

        <Section>
          <StepTitle>2. Make and Use Template</StepTitle>

          <StepContent>
            <ImagePlaceholder src={Train03} alt="Train tutorial image 03" />

            <Column>
              <SectionTitle>What to fill in</SectionTitle>

              <Column $small>
                <PrincipleText>• Template Title</PrincipleText>
                <PrincipleText>Displayed under Your Template.</PrincipleText>
              </Column>

              <Column $small>
                <PrincipleText>• Cover Video</PrincipleText>
                <PrincipleTextLight>
                  Choose one
                  <PrincipleText> test-result video </PrincipleText>that best
                  represents the complete action as the template cover.
                </PrincipleTextLight>
                <PrincipleTextLight $marginTop>
                  You can now generate with your template. You can also publish
                  your template and earn credits.
                </PrincipleTextLight>
              </Column>
            </Column>
          </StepContent>
        </Section>

        <Section>
          <StepTitle>3. Publish Template</StepTitle>

          <StepContent>
            <ImagePlaceholder src={Train04} alt="Train tutorial image 04" />

            <Column>
              <SectionTitle>What to fill in</SectionTitle>

              <Column $small>
                <PrincipleText>• Template Title</PrincipleText>
                <PrincipleText>Displayed under Your Template.</PrincipleText>
              </Column>

              <Column $small>
                <PrincipleText>• Author Name</PrincipleText>
                <PrincipleText>
                  Displayed under Your Template. Required the first time you
                  publish and cannot be changed later.
                </PrincipleText>
              </Column>

              <SectionTitle $marginTop>Review & Publishing Notes</SectionTitle>
              <Column $small>
                <PrincipleText>
                  • Templates must pass our team's review before becoming
                  publicly available
                </PrincipleText>

                <PrincipleText>
                  • When your template is used publicly, you earn credits based
                  on its age:
                </PrincipleText>

                {user?.isVipCreator ? (
                  <PrincipleText>
                    • Earn 3 credits per use during the first 30 days
                  </PrincipleText>
                ) : (
                  <PrincipleText>
                    • Earn 2 credits per use during the first 60 days
                  </PrincipleText>
                )}

                {user?.isVipCreator && (
                  <PrincipleText>
                    • Earn 2 credits per use after 30 days
                  </PrincipleText>
                )}

                <PrincipleText>
                  • Earn 1 credit per use after 60 days
                </PrincipleText>

                <PrincipleText>
                  VIP Creators receive higher earnings. Once you have 10+
                  high-quality templates live, you will be eligible to apply to
                  join VIP
                </PrincipleText>

                <PrincipleText>
                  • We review for quality standards and potential portrait
                  rights infringement
                </PrincipleText>

                <PrincipleText>
                  • Template approval is not guaranteed and we reserve final
                  discretion to remove templates at any time
                </PrincipleText>

                <PrincipleText>
                  • Once published, your template (including the model and cover
                  image/video) will be permanently public and visible to all
                  users, cannot be manually deleted or removed with account
                  deletion, and this process is irreversible
                </PrincipleText>
              </Column>
            </Column>
          </StepContent>
        </Section>
        <Column $small>
          <SectionTitle>One-line Summary</SectionTitle>
          <PrincipleText>
            • Good training videos are all you need.
          </PrincipleText>
        </Column>
      </Container>
    </MainContainer>
  );
};

export default TrainTutorialPage;
